import React from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Avatar, 
  Upload, 
  message,
  Row,
  Col,
  Descriptions,
  Statistic,
  Typography,
  Space,
  Progress
} from 'antd';
import { 
  UserOutlined, 
  UploadOutlined,
  SaveOutlined,
  LockOutlined,
  MailOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../../stores/authStore';
import { useQuery } from 'react-query';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const UserProfile = () => {
  const { user, updateProfile, loading, changePassword, changeEmail } = useAuthStore();
  const [form] = Form.useForm();
  const [pwdForm] = Form.useForm();
  const [emailForm] = Form.useForm();

  // 获取用户工作统计
  const { data: userStats } = useQuery(
    'userStats',
    () => axios
      .get('/daily-logs/stats', {
        params: {
          userId: user.id,
          startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0],
          endDate: new Date().toISOString().split('T')[0],
        },
      })
      .then((res) => res.data.data),
    { enabled: !!user?.id }
  );

  React.useEffect(() => {
    if (user) {
      form.setFieldsValue({
        realName: user.realName,
        phone: user.phone,
      });
      emailForm.setFieldsValue({
        newEmail: user.email,
      });
    }
  }, [user, form, emailForm]);

  const handleSubmit = async (values) => {
    const result = await updateProfile(values);
    if (result.success) {
      message.success('个人信息更新成功');
    } else {
      message.error(result.message);
    }
  };

  const handleAvatarChange = (info) => {
    if (info.file.status === 'uploading') {
      return;
    }
    if (info.file.status === 'done') {
      // 这里应该处理头像上传成功后的逻辑
      message.success('头像上传成功');
    }
  };

  const getRoleText = (role) => {
    const roleMap = {
      admin: '系统管理员',
      manager: '设计主管',
      designer: '设计师',
      pm: '项目经理',
    };
    return roleMap[role] || role;
  };

  const uploadProps = {
    name: 'avatar',
    action: '/api/upload/avatar',
    headers: {
      authorization: `Bearer ${user?.token}`,
    },
    onChange: handleAvatarChange,
    showUploadList: false,
  };

  /**
   * 函数：onSubmitPassword 修改密码提交
   * 说明：校验三项（当前密码/新密码/确认新密码），并调用 store 方法
   */
  const onSubmitPassword = async (values) => {
    const { currentPassword, newPassword, confirmPassword } = values;
    if (newPassword !== confirmPassword) {
      message.error('两次输入的新密码不一致');
      return;
    }
    const result = await changePassword({ currentPassword, newPassword });
    if (result.success) {
      message.success('密码修改成功');
      pwdForm.resetFields();
    } else {
      message.error(result.message || '密码修改失败');
    }
  };

  /**
   * 函数：onSubmitEmail 修改邮箱提交
   * 说明：校验新邮箱格式以及确认密码，调用 store 方法；成功后清空密码输入
   */
  const onSubmitEmail = async (values) => {
    const { newEmail, currentPassword } = values;
    if (!newEmail) {
      message.error('请输入新邮箱');
      return;
    }
    const result = await changeEmail({ newEmail, currentPassword });
    if (result.success) {
      message.success('邮箱修改成功');
      emailForm.resetFields(['currentPassword']);
    } else {
      message.error(result.message || '邮箱修改失败');
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[24, 24]}>
        {/* 左侧：个人信息 */}
        <Col xs={24} lg={16}>
          <Card
            title={
              <Space>
                <UserOutlined />
                个人信息
              </Space>
            }
            className="status-card"
          >
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              <Row gutter={24}>
                <Col xs={24} sm={8}>
                  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <Avatar
                      size={120}
                      src={user?.avatar}
                      icon={<UserOutlined />}
                      style={{ marginBottom: '16px' }}
                    />
                    <br />
                    <Upload {...uploadProps}>
                      <Button icon={<UploadOutlined />} size="small">
                        更换头像
                      </Button>
                    </Upload>
                  </div>
                </Col>

                <Col xs={24} sm={16}>
                  <Form.Item
                    name="realName"
                    label="真实姓名"
                    rules={[
                      { required: true, message: '请输入真实姓名' },
                      { min: 2, max: 50, message: '姓名长度应在2-50字符之间' },
                    ]}
                  >
                    <Input placeholder="请输入真实姓名" />
                  </Form.Item>

                  <Form.Item
                    name="phone"
                    label="联系电话"
                    rules={[
                      { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号码' },
                    ]}
                  >
                    <Input placeholder="请输入联系电话" />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SaveOutlined />}
                      loading={loading}
                    >
                      保存修改
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            {/* 基本信息展示 */}
            <Card title="账户信息" size="small" style={{ marginTop: '24px' }}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="用户名">{user?.username}</Descriptions.Item>
                <Descriptions.Item label="邮箱">{user?.email}</Descriptions.Item>
                <Descriptions.Item label="角色">{getRoleText(user?.role)}</Descriptions.Item>
                <Descriptions.Item label="职位">{user?.position}</Descriptions.Item>
                <Descriptions.Item label="日成本">¥{user?.dailyCost}</Descriptions.Item>
                <Descriptions.Item label="最后登录">
                  {user?.lastLoginAt
                    ? dayjs(user.lastLoginAt).format('YYYY-MM-DD HH:mm')
                    : '首次登录'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Card>
        </Col>

        {/* 右侧：工作统计 + 新增账户安全卡片 */}
        <Col xs={24} lg={8}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* 本月工作统计 */}
            <Card title="本月工作统计" className="status-card">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Statistic
                  title="总工作时长"
                  value={userStats?.workStats?.[0]?.totalHours || 0}
                  precision={1}
                  suffix="小时"
                  valueStyle={{ color: '#1890ff' }}
                />

                <Statistic
                  title="平均日工时"
                  value={userStats?.workStats?.[0]?.avgHours || 0}
                  precision={1}
                  suffix="小时/天"
                  valueStyle={{ color: '#52c41a' }}
                />

                <Statistic
                  title="填报天数"
                  value={userStats?.workStats?.[0]?.logCount || 0}
                  suffix="天"
                  valueStyle={{ color: '#faad14' }}
                />
              </Space>
            </Card>

            {/* 新增：修改密码 */}
            <Card
              title={
                <Space>
                  <LockOutlined />
                  修改密码
                </Space>
              }
              className="status-card"
            >
              <Form layout="vertical" form={pwdForm} onFinish={onSubmitPassword}>
                <Form.Item
                  label="当前密码"
                  name="currentPassword"
                  rules={[{ required: true, message: '请输入当前密码' }]}
                >
                  <Input.Password placeholder="请输入当前密码" autoComplete="current-password" />
                </Form.Item>
                <Form.Item
                  label="新密码"
                  name="newPassword"
                  rules={[
                    { required: true, message: '请输入新密码' },
                    { min: 6, message: '至少6位' },
                  ]}
                >
                  <Input.Password placeholder="请输入新密码，至少6位" autoComplete="new-password" />
                </Form.Item>
                <Form.Item
                  label="确认新密码"
                  name="confirmPassword"
                  dependencies={["newPassword"]}
                  rules={[{ required: true, message: '请再次输入新密码' }]}
                >
                  <Input.Password placeholder="请再次输入新密码" autoComplete="new-password" />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    提交
                  </Button>
                </Form.Item>
              </Form>
            </Card>

            {/* 新增：修改邮箱 */}
            <Card
              title={
                <Space>
                  <MailOutlined />
                  修改邮箱
                </Space>
              }
              className="status-card"
            >
              <Form layout="vertical" form={emailForm} onFinish={onSubmitEmail}>
                <Form.Item
                  label="新邮箱"
                  name="newEmail"
                  rules={[{ required: true, type: 'email', message: '请输入有效的新邮箱' }]}
                >
                  <Input placeholder="请输入新邮箱" prefix={<MailOutlined />} />
                </Form.Item>
                <Form.Item
                  label="当前密码"
                  name="currentPassword"
                  rules={[{ required: true, message: '请输入当前密码以验证身份' }]}
                >
                  <Input.Password placeholder="请输入当前密码以验证身份" />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    提交
                  </Button>
                </Form.Item>
              </Form>
            </Card>

            {/* 工作效率 */}
            <Card title="工作效率" className="status-card">
              <div style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={2} style={{ color: '#722ed1', margin: 0 }}>
                    {userStats?.workStats?.[0]?.avgHours
                      ? Math.min((userStats.workStats[0].avgHours / 8) * 100, 100).toFixed(0)
                      : 0}
                    %
                  </Title>
                  <Text type="secondary">工时利用率</Text>
                </div>

                <Progress
                  type="circle"
                  percent={
                    userStats?.workStats?.[0]?.avgHours
                      ? Math.min((userStats.workStats[0].avgHours / 8) * 100, 100)
                      : 0
                  }
                  size={120}
                  strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
                />
              </div>
            </Card>
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default UserProfile;
